import { createAction, createSlice } from '@reduxjs/toolkit';
import { reducerUpsertHelper } from '@sda/utils/store';
import { Reducers } from '@sda/enums/store';
import type { SettlementGuestPayload, SettlementState } from '@sda/interfaces/reducer/settlement.reducer';
import type { PayloadAction } from '@reduxjs/toolkit';
import type {
  MockSKU, ReconstructedTicketOrder, ShowOrderMetadata,
} from '@sda/interfaces/order';
import type { FetchDataType } from '@sda/sdk/interfaces';
import type { UpsertItem } from '@sda/utils/store/interfaces';

export const startCreatePayout = createAction<{ payoutId: string, showId: string }>(`${Reducers.SETTLEMENT}/startCreatePayout`);
export const startRefundOrder = createAction<{ orderId: string, showId: string, refundReason: string }>(`${Reducers.SETTLEMENT}/startRefundOrder`);
export const startUpdateSettlementGuest = createAction<SettlementGuestPayload>(`${Reducers.SETTLEMENT}/startUpdateSettlementGuest`);
export const startSendTicketRedemptionEmail = createAction<string>(`${Reducers.SETTLEMENT}/startSendTicketRedemptionEmail`);
export const startUpdatePurchaserEmail = createAction<{ orderId: string, email: string }>(`${Reducers.SETTLEMENT}/startUpdatePurchaserEmail`);

export const startFetchSettlementDetails = createAction<string>(`${Reducers.SETTLEMENT}/startFetchSettlementDetails`);
export const unsubscribeSettlement = createAction<string>(`${Reducers.SETTLEMENT}/unsubscribeSettlement`);

const initialState: SettlementState = {
  isLoaded: false,
  isLoading: false,
  orderItems: null,
  sku: null,
  current: null,
  metadata: null,
  countryMetadata: null,
  productMetadata: null,
};

const settlementSlice = createSlice({
  initialState,
  name: Reducers.SETTLEMENT,
  reducers: {
    resetSettlement: (state) => {
      state.orderItems = null;
      state.isLoaded = false;
      state.isLoading = true;
      state.sku = null;
      state.current = null;
      state.metadata = null;
    },
    setSettlementCurrentId: (state, action: PayloadAction<string>) => {
      state.current = action.payload;
    },
    setSettlementDetailsLoaded: (state) => {
      state.isLoading = false;
      state.isLoaded = true;
    },
    setSettlementOrderItems: (state, action: PayloadAction<ReconstructedTicketOrder[]>) => {
      state.orderItems = action.payload;
    },
    setSettlementStripeSKU: (state, action: PayloadAction<MockSKU>) => {
      state.sku = action.payload;
    },
    upsertOrderMetadata: (state, action: PayloadAction<UpsertItem<ShowOrderMetadata & FetchDataType>>) => {
      state.metadata = reducerUpsertHelper(state.metadata, action.payload, 'showId');
    },
    upsertSettlementCountryMetadata: (state, action: PayloadAction<UpsertItem<ShowOrderMetadata>>) => {
      state.countryMetadata = reducerUpsertHelper(state.countryMetadata, action.payload, 'id');
    },
    upsertSettlementProductMetadata: (state, action: PayloadAction<UpsertItem<ShowOrderMetadata>>) => {
      state.productMetadata = reducerUpsertHelper(state.productMetadata, action.payload, 'id');
    },
  },
});

export const {
  resetSettlement, setSettlementCurrentId, setSettlementDetailsLoaded, setSettlementOrderItems,
  setSettlementStripeSKU, upsertOrderMetadata, upsertSettlementCountryMetadata, upsertSettlementProductMetadata,
} = settlementSlice.actions;

export default settlementSlice.reducer;
